import { CSSProperties } from 'react';
import { CHIP_VARIANT } from 'framework/chip/enums';
import { ChipTheme } from 'framework/chip/types';
import { field_border_theme, field_color_theme } from 'framework/field/constants';
import { colors } from 'framework/theme/constants/colors';
import { ThemeVariant } from 'framework/theme/types';

import { VARIANT } from '../../constants';

import { DARK_CHIP_BORDER, DARK_CHIP_COLOR, DARK_FILL } from './constants';

export const getChipColor = (
  variant: CHIP_VARIANT,
  theme_variant: ThemeVariant
): CSSProperties['color'] => {
  switch (variant) {
    case CHIP_VARIANT.RED:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.RED];
      }
      return colors.red[13];
    case CHIP_VARIANT.GREEN:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.GREEN];
      }
      return colors.green[13];
    case CHIP_VARIANT.BLUE:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.BLUE];
      }
      return colors.blue[13];
    case CHIP_VARIANT.NAVY:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.NAVY];
      }
      return colors.navy[7];
    case CHIP_VARIANT.YELLOW:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.YELLOW];
      }
      return colors.yellow[6];
    case CHIP_VARIANT.AQUA:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.AQUA];
      }
      return colors.aqua[7];
    case CHIP_VARIANT.LIVID:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.LIVID];
      }
      return colors.livid[6];
    case CHIP_VARIANT.PLATINUM:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.PLATINUM];
      }
      return colors.dark[7];
    case CHIP_VARIANT.TEAL:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.TEAL];
      }
      return colors.teal[6];
    case CHIP_VARIANT.PURPLE:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.PURPLE];
      }
      return colors.purple[8];
    case CHIP_VARIANT.ORANGE:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.ORANGE];
      }
      return colors.orange[11];
    case CHIP_VARIANT.PINK:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.PINK];
      }
      return colors.pink[5];
    case CHIP_VARIANT.LIGHT:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.LIGHT];
      }
      return colors.dark[3];
    case CHIP_VARIANT.DARK:
      if (theme_variant === VARIANT.DARK) {
        return DARK_CHIP_COLOR[CHIP_VARIANT.DARK];
      }
      return colors.light[1];
    default:
      return colors.light[1];
  }
};

export const getIconColor = (
  variant: CHIP_VARIANT,
  theme_variant: ThemeVariant
): CSSProperties['color'] => {
  switch (variant) {
    case CHIP_VARIANT.RED:
      return DARK_CHIP_BORDER[CHIP_VARIANT.RED];
    case CHIP_VARIANT.GREEN:
      return DARK_CHIP_BORDER[CHIP_VARIANT.GREEN];
    case CHIP_VARIANT.BLUE:
      return DARK_CHIP_BORDER[CHIP_VARIANT.BLUE];
    case CHIP_VARIANT.NAVY:
      return DARK_CHIP_BORDER[CHIP_VARIANT.NAVY];
    case CHIP_VARIANT.YELLOW:
      return DARK_CHIP_BORDER[CHIP_VARIANT.YELLOW];
    case CHIP_VARIANT.AQUA:
      return DARK_CHIP_BORDER[CHIP_VARIANT.AQUA];
    case CHIP_VARIANT.LIVID:
      return DARK_CHIP_BORDER[CHIP_VARIANT.LIVID];
    case CHIP_VARIANT.PLATINUM:
      return DARK_CHIP_BORDER[CHIP_VARIANT.PLATINUM];
    case CHIP_VARIANT.TEAL:
      return DARK_CHIP_BORDER[CHIP_VARIANT.TEAL];
    case CHIP_VARIANT.PURPLE:
      return DARK_CHIP_BORDER[CHIP_VARIANT.PURPLE];
    case CHIP_VARIANT.ORANGE:
      return DARK_CHIP_BORDER[CHIP_VARIANT.ORANGE];
    case CHIP_VARIANT.PINK:
      return DARK_CHIP_BORDER[CHIP_VARIANT.PINK];
    case CHIP_VARIANT.LIGHT:
      return DARK_CHIP_BORDER[CHIP_VARIANT.LIGHT];
    case CHIP_VARIANT.DARK:
      return DARK_CHIP_BORDER[CHIP_VARIANT.DARK];
    default:
      return theme_variant !== VARIANT.DARK ? colors.light[9] : colors.dark[3];
  }
};

export const getChipFill = (
  variant: CHIP_VARIANT,
  theme_variant: ThemeVariant
): Pick<ChipTheme, 'border' | 'background'> => {
  switch (variant) {
    case CHIP_VARIANT.RED:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.RED]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.RED]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.RED]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.red[2],
          focus: colors.red[2],
          hover: colors.red[2],
        },
        border: {
          normal: `1px solid ${colors.red[4]}`,
          focus: `1px solid ${colors.red[4]}`,
          hover: `1px solid ${colors.red[4]}`,
        },
      };
    case CHIP_VARIANT.GREEN:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.GREEN]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.GREEN]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.GREEN]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.green[2],
          focus: colors.green[2],
          hover: colors.green[2],
        },
        border: {
          normal: `1px solid ${colors.green[4]}`,
          focus: `1px solid ${colors.green[4]}`,
          hover: `1px solid ${colors.green[4]}`,
        },
      };
    case CHIP_VARIANT.BLUE:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.BLUE]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.BLUE]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.BLUE]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.blue[2],
          focus: colors.blue[2],
          hover: colors.blue[2],
        },
        border: {
          normal: `1px solid ${colors.blue[4]}`,
          focus: `1px solid ${colors.blue[4]}`,
          hover: `1px solid ${colors.blue[4]}`,
        },
      };
    case CHIP_VARIANT.NAVY:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.NAVY]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.NAVY]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.NAVY]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.navy[2],
          focus: colors.navy[2],
          hover: colors.navy[2],
        },
        border: {
          normal: `1px solid ${colors.navy[4]}`,
          focus: `1px solid ${colors.navy[4]}`,
          hover: `1px solid ${colors.navy[4]}`,
        },
      };
    case CHIP_VARIANT.YELLOW:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.YELLOW]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.YELLOW]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.YELLOW]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.yellow[1],
          focus: colors.yellow[1],
          hover: colors.yellow[1],
        },
        border: {
          normal: `1px solid ${colors.yellow[3]}`,
          focus: `1px solid ${colors.yellow[3]}`,
          hover: `1px solid ${colors.yellow[3]}`,
        },
      };
    case CHIP_VARIANT.AQUA:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.AQUA]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.AQUA]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.AQUA]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.aqua[2],
          focus: colors.aqua[2],
          hover: colors.aqua[2],
        },
        border: {
          normal: `1px solid ${colors.aqua[4]}`,
          focus: `1px solid ${colors.aqua[4]}`,
          hover: `1px solid ${colors.aqua[4]}`,
        },
      };
    case CHIP_VARIANT.LIVID:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.LIVID]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.LIVID]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.LIVID]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.livid[2],
          focus: colors.livid[2],
          hover: colors.livid[2],
        },
        border: {
          normal: `1px solid ${colors.livid[4]}`,
          focus: `1px solid ${colors.livid[4]}`,
          hover: `1px solid ${colors.livid[4]}`,
        },
      };
    case CHIP_VARIANT.PLATINUM:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PLATINUM]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PLATINUM]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PLATINUM]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.platinum[1],
          focus: colors.platinum[1],
          hover: colors.platinum[1],
        },
        border: {
          normal: `1px solid ${colors.platinum[3]}`,
          focus: `1px solid ${colors.platinum[3]}`,
          hover: `1px solid ${colors.platinum[3]}`,
        },
      };
    case CHIP_VARIANT.TEAL:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.TEAL]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.TEAL]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.TEAL]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.teal[2],
          focus: colors.teal[2],
          hover: colors.teal[2],
        },
        border: {
          normal: `1px solid ${colors.teal[4]}`,
          focus: `1px solid ${colors.teal[4]}`,
          hover: `1px solid ${colors.teal[4]}`,
        },
      };
    case CHIP_VARIANT.PURPLE:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PURPLE]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PURPLE]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PURPLE]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.purple[2],
          focus: colors.purple[2],
          hover: colors.purple[2],
        },
        border: {
          normal: `1px solid ${colors.purple[4]}`,
          focus: `1px solid ${colors.purple[4]}`,
          hover: `1px solid ${colors.purple[4]}`,
        },
      };
    case CHIP_VARIANT.ORANGE:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.ORANGE]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.ORANGE]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.ORANGE]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.orange[2],
          focus: colors.orange[2],
          hover: colors.orange[2],
        },
        border: {
          normal: `1px solid ${colors.orange[4]}`,
          focus: `1px solid ${colors.orange[4]}`,
          hover: `1px solid ${colors.orange[4]}`,
        },
      };
    case CHIP_VARIANT.PINK:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PINK]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PINK]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.PINK]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.pink[1],
          focus: colors.pink[1],
          hover: colors.pink[1],
        },
        border: {
          normal: `1px solid ${colors.pink[3]}`,
          focus: `1px solid ${colors.pink[3]}`,
          hover: `1px solid ${colors.pink[3]}`,
        },
      };
    case CHIP_VARIANT.LIGHT:
      if (theme_variant === VARIANT.DARK) {
        return {
          background: DARK_FILL,
          border: {
            normal: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.LIGHT]}`,
            focus: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.LIGHT]}`,
            hover: `1px solid ${DARK_CHIP_BORDER[CHIP_VARIANT.LIGHT]}`,
          },
        };
      }
      return {
        background: {
          normal: colors.light[2],
          focus: colors.light[2],
          hover: colors.light[2],
        },
        border: {
          normal: `1px solid ${colors.light[4]}`,
          focus: `1px solid ${colors.light[4]}`,
          hover: `1px solid ${colors.light[4]}`,
        },
      };
    case CHIP_VARIANT.DARK:
      return {
        background: DARK_FILL,
        border: {
          normal: field_border_theme.normal,
          focus: field_border_theme.focus,
          hover: field_border_theme.hover,
        },
      };
    default:
      return {
        background: {
          normal: field_color_theme.normal,
          focus: field_color_theme.focus,
          hover: field_color_theme.hover,
        },
        border: {
          normal: field_border_theme.normal,
          focus: field_border_theme.focus,
          hover: field_border_theme.hover,
        },
      };
  }
};
